import { ChainProtocol } from '@archax/shared-types'
import { cloneElement, ReactElement } from 'react'
import {
  PROTOCOLS_CAN_BE_CONFIGURED,
  PROTOCOLS_WITH_PRIVACY_ENABLED,
  PROTOCOLS_WITH_SUPPORT_TO_BLACKLIST,
  PROTOCOLS_WITH_WHITELIST_REQUIRED,
} from '../constants'
import { isEVMProtocol } from './token-options'

export enum FeatureMap {
  PRIVACY,
  WHITELIST,
  BLACKLIST,
  UPLOAD_DOCS,
  CONFIGURATION,
  PAUSE_TOKEN,
  UNPAUSE_TOKEN,
}

const ChainFeatureToHideOrDisableMap = Object.values(ChainProtocol).reduce((t: any, k) => {
  t[k] = []
  return t
}, {}) as Record<string, FeatureMap[]>
Object.keys(ChainFeatureToHideOrDisableMap).forEach((k) => {
  if (PROTOCOLS_WITH_WHITELIST_REQUIRED.includes(k as ChainProtocol)) {
    ChainFeatureToHideOrDisableMap[k].push(FeatureMap.WHITELIST)
  }
})
Object.keys(ChainFeatureToHideOrDisableMap).forEach((k) => {
  if (!PROTOCOLS_WITH_SUPPORT_TO_BLACKLIST.includes(k as ChainProtocol)) {
    ChainFeatureToHideOrDisableMap[k].push(FeatureMap.BLACKLIST)
  }
})
Object.keys(ChainFeatureToHideOrDisableMap).forEach((k) => {
  if (!PROTOCOLS_WITH_PRIVACY_ENABLED.includes(k as ChainProtocol)) {
    ChainFeatureToHideOrDisableMap[k].push(FeatureMap.PRIVACY)
  }
})
Object.keys(ChainFeatureToHideOrDisableMap).forEach((k) => {
  if (
    !isEVMProtocol(k as ChainProtocol) &&
    ![ChainProtocol.Aptos, ChainProtocol.Stellar].includes(k as ChainProtocol)
  ) {
    ChainFeatureToHideOrDisableMap[k].push(FeatureMap.UPLOAD_DOCS)
  }
})
Object.keys(ChainFeatureToHideOrDisableMap).forEach((k) => {
  if (!isEVMProtocol(k as ChainProtocol) && ![ChainProtocol.Aptos].includes(k as ChainProtocol)) {
    ChainFeatureToHideOrDisableMap[k].push(FeatureMap.CONFIGURATION)
  }
})
Object.keys(ChainFeatureToHideOrDisableMap).forEach((k) => {
  if ([ChainProtocol.XRP_Ledger].includes(k as ChainProtocol)) {
    ChainFeatureToHideOrDisableMap[k].push(FeatureMap.PAUSE_TOKEN)
    ChainFeatureToHideOrDisableMap[k].push(FeatureMap.UNPAUSE_TOKEN)
  }
})
export { ChainFeatureToHideOrDisableMap }

type ShouldRenderProps = {
  skipIfEmpty?: boolean
  feature: FeatureMap
  protocol?: string | ChainProtocol
}
export function shouldRender({ skipIfEmpty = true, protocol, feature }: ShouldRenderProps): boolean {
  if (protocol) {
    if (ChainFeatureToHideOrDisableMap[protocol.toString()]?.includes(feature)) {
      return false
    }
  } else if (!skipIfEmpty) {
    return false
  }
  return true
}

type Props = {
  skipIfEmpty?: boolean
  feature: FeatureMap
  protocol?: string | ChainProtocol
  children: ReactElement
}

export function DisableElement({ skipIfEmpty = true, protocol, feature, children }: Props): ReactElement {
  if (protocol) {
    if (ChainFeatureToHideOrDisableMap[protocol.toString()]?.includes(feature)) {
      return cloneElement(children, { ...children.props, ...{ disabled: true } })
    }
  } else if (!skipIfEmpty) {
    return cloneElement(children, { ...children.props, ...{ disabled: true } })
  }

  return children
}

export function HideElement({ skipIfEmpty = true, protocol, feature, children }: Props): ReactElement {
  if (protocol) {
    if (ChainFeatureToHideOrDisableMap[protocol.toString()]?.includes(feature)) {
      return <></>
    }
  } else if (!skipIfEmpty) {
    return <></>
  }
  return children
}

/**
 * Reports whether an operator can turn the whitelist and blacklist on and off after deployment.
 *
 * The protocol arrives as a `ChainProtocol` value, which is what the API stores and sends, so it is
 * compared against the list directly. Indexing the enum by that value instead looks the value up as
 * a key, and a string enum carries no reverse mapping, so every real protocol resolved to undefined
 * and this returned false for all of them.
 */
export function protocolsCanBeConfigured(protocol?: string | ChainProtocol): boolean {
  if (!protocol) {
    return false
  }
  return PROTOCOLS_CAN_BE_CONFIGURED.includes(protocol as ChainProtocol)
}
